import React from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: HomeIcon, exact: true },
  { path: '/admin/users', label: 'Users', icon: UsersIcon },
  { path: '/admin/families', label: 'Families', icon: ChartBarIcon },
  { path: '/admin/data', label: 'All Data', icon: CircleStackIcon },
  { path: '/admin/logs', label: 'System Logs', icon: DocumentTextIcon },
];

export default function AdminLayout() {
  const location = useLocation();
  const { user } = useAuthStore();

  // Redirect non-admins
  if (!user?.is_superadmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="p-1.5 rounded-lg text-surface-400 hover:text-white hover:bg-surface-700 transition-colors"
                title="Back to App"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="w-6 h-6 text-amber-500" />
                <span className="font-bold text-lg text-white">Admin Panel</span>
              </div>
            </div>
            <div className="text-sm text-surface-400">
              Logged in as <span className="text-amber-400 font-medium">{user.username}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-slate-800/50 border-b border-surface-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2">
            {navItems.map((item) => {
              const isActive = item.exact 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'text-surface-400 hover:text-white hover:bg-surface-700'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>
    </div>
  );
}

